// Offline mutation queue (Drive mode) — stores edit / archive / delete
// operations attempted while the browser is offline so they can be
// replayed against the API when the network returns. Add-new-plant and
// photo/care-log/merge operations are intentionally NOT queued (they
// need live Drive uploads); those still block while offline.
//
// Each entry stores exactly what's needed to replay the original
// request: method, url, and the JSON body. The Authorization header is
// deliberately NOT stored — a fresh session token is attached at flush
// time so an expired token in the queue can't leak or fail the replay.

const QUEUE_DB_NAME = 'PlantInventoryQueue';
const QUEUE_DB_VERSION = 1;
const QUEUE_STORE = 'pending';

export const QUEUE_CHANGED_EVENT = 'plant-offline-queue-changed';

function openQueue() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(QUEUE_DB_NAME, QUEUE_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'key', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('queue-open-failed'));
  });
}

function notifyChanged() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
    }
  } catch { /* no-op */ }
}

/**
 * Append a mutation to the queue. Returns the assigned key (or null on
 * failure — enqueue is best-effort, callers still update the UI).
 */
export async function enqueueMutation(entry) {
  try {
    const db = await openQueue();
    const key = await new Promise((resolve, reject) => {
      const tx = db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      const req = store.add({ ...entry, created_at: entry.created_at || Date.now() });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    db.close();
    notifyChanged();
    return key;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.debug('offline queue enqueue failed (non-fatal):', err?.message || err);
    }
    return null;
  }
}

/** Read all pending mutations for a user, oldest first. */
export async function getPendingMutations(userKey) {
  try {
    const db = await openQueue();
    const all = await new Promise((resolve, reject) => {
      const tx = db.transaction(QUEUE_STORE, 'readonly');
      const store = tx.objectStore(QUEUE_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return all
      .filter((e) => !userKey || e.user_key === userKey)
      .sort((a, b) => (a.key || 0) - (b.key || 0));
  } catch {
    return [];
  }
}

/** Count pending mutations for a user. */
export async function countPendingMutations(userKey) {
  const list = await getPendingMutations(userKey);
  return list.length;
}

/** Delete a single mutation by its key. */
export async function removeMutation(key) {
  try {
    const db = await openQueue();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      const req = store.delete(key);
      req.onsuccess = resolve;
      req.onerror = () => reject(req.error);
    });
    db.close();
    notifyChanged();
    return true;
  } catch {
    return false;
  }
}

/** Merge a partial patch (e.g. bump `attempts`) into an existing entry. */
export async function updateMutation(key, patch) {
  try {
    const db = await openQueue();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      const getReq = store.get(key);
      getReq.onsuccess = () => {
        const entry = getReq.result;
        if (!entry) { resolve(); return; }
        const putReq = store.put({ ...entry, ...patch });
        putReq.onsuccess = resolve;
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
    db.close();
    return true;
  } catch {
    return false;
  }
}

/** Delete every pending mutation for a user. Returns the deleted entries. */
export async function clearAllMutations(userKey) {
  const entries = await getPendingMutations(userKey);
  for (const entry of entries) {
    await removeMutation(entry.key);
  }
  return entries;
}
